"use client";

/**
 * OpeningTimesBanner.tsx
 *
 * The dark left panel of the Reservation section.
 * Shows the restaurant's opening hours schedule and phone number.
 * Content is dynamic (from CMS) with sensible fallback defaults.
 */

import React from "react";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface ScheduleItem {
  day: string;
  time: string;
}

interface OpeningTimesBannerProps {
  subtitle: string;
  title: string;
  schedule: ScheduleItem[];
  callUsTitle: string;
  phoneNumber: string;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const OpeningTimesBanner = ({
  subtitle,
  title,
  schedule,
  callUsTitle,
  phoneNumber,
}: OpeningTimesBannerProps) => {
  return (
    <div className="lg:w-[40%] text-center relative mb-[30px] lg:mb-0 bg-[#1b1b1b] p-[20px] md:p-[30px]">
      {/* Double-border container — matches the live ElRoyale reference */}
      <div className="relative border border-[#c59d5f] h-full w-full">
        <div className="absolute -top-[4px] bottom-[0px] left-[2px] right-[2px] h-[calc(100%+8px)] border border-[#c59d5f] rounded-[2px] pointer-events-none z-0" />

        <div className="relative z-[2] pt-[45px] px-[20px] pb-[30px] md:pt-[65px] md:px-[40px] md:pb-[50px] flex flex-col h-full justify-center">
          <span className="font-sacramento text-[30px] text-[#c59d5f] block mb-0 leading-none">
            {subtitle}
          </span>
          <h5 className="font-cormorant text-[32px] font-bold text-white mb-[26px] leading-[1.2]">
            {title}
          </h5>

          {/* Opening hours list */}
          <ul className="mb-[26px] list-none p-0 text-[#9b9b9b]">
            {schedule.map((item, idx) => (
              <li
                key={idx}
                className={`flex justify-between leading-[40px] ${
                  idx !== schedule.length - 1
                    ? "border-b border-dashed border-[#494b4f]"
                    : ""
                }`}
              >
                <span>{item.day}</span>
                <span>{item.time}</span>
              </li>
            ))}
          </ul>

          {/* Call us */}
          <div>
            <span className="font-sacramento text-[30px] text-[#c59d5f] block mb-0 leading-none">
              {callUsTitle}
            </span>
            <a
              href={`tel:${phoneNumber}`}
              className="font-cormorant text-[27px] text-white mb-[6px] font-medium block transition-colors hover:text-[#c59d5f]"
            >
              {phoneNumber}
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OpeningTimesBanner;
